import React from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper, Stack, Button, Fade,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Chip,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { modernTheme, formatMoney, iconBoxSx } from './theme';

const CommissionWidget = ({ commissionData, commissionHistory, isMobile, userRole, leaderData }) => {
  const navigate = useNavigate();
  const isLeader = userRole === 'leader_live';

  if (!commissionData || (commissionData.commissionPercent === 0 && commissionData.totalGMV === 0 && !isLeader)) {
    return null;
  }

  // For leader, also check if there's any team data
  if (isLeader && !leaderData && (!commissionData || commissionData.totalGMV === 0)) {
    return null;
  }

  return (
    <Fade in timeout={800}>
      <Card sx={{
        borderRadius: 3,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        boxShadow: modernTheme.shadows.md,
        background: modernTheme.gradients.card,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          p: { xs: 2, md: 2.5 },
          borderBottom: `1px solid ${modernTheme.colors.gray[200]}`,
          background: modernTheme.colors.gray[50],
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={iconBoxSx('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 40)}>
            <MonetizationOnIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.2 }}>
              {isLeader ? 'Hoa hồng cá nhân tháng này' : 'Hoa hồng tháng này'}
            </Typography>
            <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500] }}>
              {isLeader
                ? 'Tính khi Lead trực tiếp live, dựa trên GMV cá nhân'
                : commissionData.isTimeBased ? 'Tính theo khung giờ' : 'Tạm tính theo doanh thu đã duyệt'}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          {/* Summary Cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, textAlign: 'center',
                bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
              }}>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                  {isLeader ? 'GMV cá nhân' : 'Doanh thu tháng'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e40af', mt: 0.5, fontSize: '1.1rem' }}>
                  {formatMoney(commissionData.totalGMV)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, textAlign: 'center',
                bgcolor: '#fef3c7', border: '1px solid #fde68a',
              }}>
                <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                  {commissionData.isTimeBased ? '% TB theo giờ' : '% Hoa hồng'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e', mt: 0.5, fontSize: '1.1rem' }}>
                  {commissionData.commissionPercent}%
                  {commissionData.isTimeBased && (
                    <AccessTimeIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#d97706' }} />
                  )}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, textAlign: 'center',
                bgcolor: '#ecfdf5', border: '1px solid #a7f3d0',
              }}>
                <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                  {isLeader ? 'HH cá nhân tạm tính' : 'Hoa hồng tạm tính'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46', mt: 0.5, fontSize: '1.1rem' }}>
                  {formatMoney(commissionData.commissionAmount)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Per-session breakdown for time-based */}
          {commissionData.isTimeBased && commissionData.revenueBreakdown?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.gray[700], textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                Chi tiết theo phiên
              </Typography>
              <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${modernTheme.colors.gray[200]}`, overflow: 'hidden' }}>
                {commissionData.revenueBreakdown.map((rb, idx) => (
                  <Box key={idx} sx={{ px: 1.5, py: 1, borderBottom: idx < commissionData.revenueBreakdown.length - 1 ? `1px solid ${modernTheme.colors.gray[100]}` : 'none', '&:hover': { bgcolor: '#fffbeb' } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: modernTheme.colors.gray[700] }}>
                        {formatMoney(rb.totalSales)}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip label={`${rb.effectiveRate}%`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#065f46', fontSize: '0.72rem' }}>
                          {formatMoney(rb.commissionAmount)}
                        </Typography>
                      </Stack>
                    </Stack>
                    {rb.isTimeBased && rb.breakdown?.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                        {rb.breakdown.map((b, bi) => (
                          <Typography key={bi} variant="caption" sx={{ fontSize: '0.58rem', color: '#6b7280' }}>
                            {b.startTime}-{b.endTime} [{b.slotName}] {b.rate}%: {formatMoney(b.commission)}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          )}

          {/* Recent History */}
          {commissionHistory.length > 1 && (
            <Box sx={{ mt: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: modernTheme.colors.gray[700], textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                  Lịch sử gần đây
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/dashboard/history')}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.7rem', minWidth: 'auto', py: 0 }}
                >
                  Xem đầy đủ →
                </Button>
              </Stack>

              {isLeader ? (
                /* === LEADER HISTORY TABLE === */
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e9d5ff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#faf5ff' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>Tháng</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>Team GMV</TableCell>
                        {!isMobile && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>HH Team</TableCell>}
                        {!isMobile && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>Thưởng</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>HH CN</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, color: '#581c87' }}>Tổng</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commissionHistory.slice(0, 3).map((item) => {
                        const personal = item.personal || {};
                        const team = item.team || {};
                        const teamBonusTotal = (team.teamCommission || 0) + (team.growthBonus || 0) + (team.tiktokKpiBonus || 0);
                        return (
                          <TableRow key={item.monthKey} hover>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                              {item.monthKey}
                              {personal.isSnapshot && (
                                <Chip label="Chốt" size="small" color="success" variant="outlined"
                                  sx={{ ml: 0.5, height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', py: 1 }}>
                              {formatMoney(team.teamTotalGMV || 0)}
                            </TableCell>
                            {!isMobile && (
                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', py: 1 }}>
                                {formatMoney(team.teamCommission || 0)}
                              </TableCell>
                            )}
                            {!isMobile && (
                              <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>
                                <Stack spacing={0.25} alignItems="flex-end">
                                  {(team.growthBonus || 0) > 0 && (
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#059669', fontWeight: 600 }}>
                                      Vượt: {formatMoney(team.growthBonus)}
                                    </Typography>
                                  )}
                                  {(team.tiktokKpiBonus || 0) > 0 && (
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#059669', fontWeight: 600 }}>
                                      TikTok: {formatMoney(team.tiktokKpiBonus)}
                                    </Typography>
                                  )}
                                  {teamBonusTotal === 0 && (
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: modernTheme.colors.gray[400] }}>
                                      —
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                            )}
                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#92400e', py: 1 }}>
                              {formatMoney(personal.commissionAmount || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#065f46', py: 1 }}>
                              {formatMoney(item.totalIncome || 0)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* === NORMAL USER HISTORY TABLE === */
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${modernTheme.colors.gray[200]}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: modernTheme.colors.gray[50] }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Tháng</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Doanh thu</TableCell>
                        {!isMobile && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>%</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Hoa hồng</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commissionHistory.slice(0, 3).map((item) => (
                        <TableRow key={item.monthKey} hover>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                            {item.monthKey}
                            {item.isSnapshot && (
                              <Chip label="Chốt" size="small" color="success" variant="outlined"
                                sx={{ ml: 0.5, height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e40af', py: 1 }}>
                            {formatMoney(item.totalGMV)}
                          </TableCell>
                          {!isMobile && (
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>{item.commissionPercent}%</TableCell>
                          )}
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#065f46', py: 1 }}>
                            {formatMoney(item.commissionAmount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

export default CommissionWidget;
